import { toLoggableEditorError } from '@/components/tiptap/utils/loggable-editor-error';
import { logger } from '@/utils/logger/logger';
import {
  renderStreamingMarkdownToHtml,
  type SafeEditorErrorSummary,
} from '@heidi/editor/core';
import { useControlledContentSync, useStreamedTextFade } from '@heidi/editor/react';
import { type Editor } from '@tiptap/react';
import { useCallback } from 'react';

import {
  emitNoteEditorRenderFailedOnce,
  MARKDOWN_NATIVE_FINALIZE_FAILED_CODE,
  MARKDOWN_NATIVE_RENDER_FAILED_CODE,
  type NoteEditorRenderContentType,
  type NoteEditorSurface,
} from '../../telemetry/emit-note-editor-render-failed';

/** Alias of the shared render-failure format union so the two can't drift. */
export type MarkdownNativeContentType = NoteEditorRenderContentType;

// The controlled-synchronization primitive lives in @heidi/editor/react (task
// 6.1); the streaming render transform in /core (task 5.6). Both are
// re-exported here so existing importers keep their paths.
export { renderStreamingMarkdownToHtml };

/**
 * App-side controlled-synchronization adapter over `@heidi/editor/react`.
 *
 * The package owns the synchronization behaviour — hydration, the focused-echo
 * guard, forced external apply, the streaming throttle and the canonical
 * finalize/retry — and reports a failed apply instead of logging it. This
 * adapter owns the product side of that failure: the developer log line and
 * the alertable `note_editor_render_failed` event, with the same payloads and
 * the same error codes as before the move.
 */
export const useMarkdownNativeContentSync = ({
  editor,
  content,
  contentType = 'markdown',
  surface,
  isStreaming = false,
  fadeStreamedText = false,
  forceApply = false,
}: {
  editor: Editor | null | undefined;
  content: string;
  contentType?: MarkdownNativeContentType;
  /** Note-tab SLI membership; only the note editor supplies it. */
  surface?: NoteEditorSurface;
  isStreaming?: boolean;
  fadeStreamedText?: boolean;
  forceApply?: boolean;
}) => {
  const onApplyFailure = useCallback(
    ({
      error,
      contentType: failedContentType,
      contentLength,
      stage,
    }: Parameters<
      NonNullable<Parameters<typeof useControlledContentSync>[0]['onApplyFailure']>
    >[0]) => {
      // Typed safe envelope only (task 4.10): the package hands over the
      // content-free summary, never the raw editor error — narrowed here
      // because the envelope passes a writable `Error#name` through and this
      // callback, not `onLog`, is the wired path.
      logger.error('Failed to set markdown-native editor content', {
        error: toLoggableEditorError(error, 'content_apply_failed'),
        contentType: failedContentType,
        contentLength,
      });
      // Deduped per editor per code, like the shared apply shell — the arms
      // are only comparable if both count editors rather than applies. The
      // package only reports a failure for an editor it applied to, so this is
      // never reached without one. A failed finalize gets its own code.
      if (!editor) return;
      emitNoteEditorRenderFailedOnce(editor, {
        editorType: 'markdown_native',
        contentType: failedContentType,
        contentLength,
        errorCode:
          stage === 'finalize'
            ? MARKDOWN_NATIVE_FINALIZE_FAILED_CODE
            : MARKDOWN_NATIVE_RENDER_FAILED_CODE,
        surface,
      });
    },
    [editor, surface]
  );

  // Before 6.1b this hook called the app-side apply *shell*, which always wired
  // this log; the packaged hook calls the pure transform, so the reporter has
  // to carry it. Same message and same payload as the shell emits.
  const onSelectionRestoreFailure = useCallback((error: SafeEditorErrorSummary) => {
    logger.error('Failed to restore editor selection after setContent', {
      error: toLoggableEditorError(error, 'selection_restore_failed'),
    });
  }, []);

  // Declared before content sync so stream-end deactivation runs before the
  // canonical finalize apply. The core sync path remains unaware of the fade.
  useStreamedTextFade({ editor, enabled: fadeStreamedText, isStreaming });

  useControlledContentSync({
    editor,
    content,
    contentType,
    isStreaming,
    forceApply,
    onApplyFailure,
    onSelectionRestoreFailure,
  });
};
